import { useToast } from '@chakra-ui/react';
import { useContext } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import {
  getApolicesAVencer,
  getClientes,
  getContratos90Dias,
  getContratosAguardandoReajuste,
  getContratosAndamento,
  getEquipamentosContratados,
  getFaturasEmAtraso,
  getHome,
  getPieChart,
  getValoresFaturados,
} from '~/services';
import { Contratos90Dias, Home, ValoresFaturados } from '~/types/home';
import { InsuranceList } from '~/types/insurance';
import { Pagination } from '~/types';
import { useRoleCan } from '~/hooks';

export function useHome(
  invoiceStatus = '',
  options: UseQueryOptions<Home, unknown> = {},
): UseQueryResult<Home> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<Home, unknown>(
    ['home', invoiceStatus, { arrGroups, arrHeadOffices, arrBrancches }],
    () =>
      getHome(invoiceStatus, {
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function usePieChart(
  invoiceStatus = '',
  options: UseQueryOptions<any[], unknown> = {},
): UseQueryResult<any[]> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<any[], unknown>(
    ['piechart', invoiceStatus, { arrGroups, arrHeadOffices, arrBrancches }],
    () =>
      getPieChart(invoiceStatus, {
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useContratosEmAndamento(
  invoiceStatus = '',
  options: UseQueryOptions<number, unknown> = {},
): UseQueryResult<number> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<number, unknown>(
    [
      'contratos_em_andamento',
      invoiceStatus,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () =>
      getContratosAndamento(invoiceStatus, {
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useClientes(
  invoiceStatus = '',
  options: UseQueryOptions<number, unknown> = {},
): UseQueryResult<number> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<number, unknown>(
    [
      'clientes_home',
      invoiceStatus,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () =>
      getClientes(invoiceStatus, {
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useFaturasEmAtraso(
  invoiceStatus = '',
  options: UseQueryOptions<number, unknown> = {},
): UseQueryResult<number> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<number, unknown>(
    [
      'faturas_atraso',
      invoiceStatus,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () =>
      getFaturasEmAtraso(invoiceStatus, {
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useContratos90Dias(
  invoiceStatus = '',
  options: UseQueryOptions<Contratos90Dias, unknown> = {},
): UseQueryResult<Contratos90Dias> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<Contratos90Dias, unknown>(
    [
      'contratos_noventa_dias',
      invoiceStatus,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () =>
      getContratos90Dias(invoiceStatus, {
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useContratosAguardandoReajuste(
  invoiceStatus = '',
  options: UseQueryOptions<number, unknown> = {},
): UseQueryResult<number> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<number, unknown>(
    [
      'contratos_aguardando_reajuste',
      invoiceStatus,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () =>
      getContratosAguardandoReajuste(invoiceStatus, {
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useEquipamentosContratados(
  invoiceStatus = '',
  options: UseQueryOptions<number, unknown> = {},
): UseQueryResult<number> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<number, unknown>(
    [
      'equipamentos_contratados',
      invoiceStatus,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () =>
      getEquipamentosContratados(invoiceStatus, {
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useValoresFaturados(
  invoiceStatus = '',
  options: UseQueryOptions<ValoresFaturados, unknown> = {},
): UseQueryResult<ValoresFaturados> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<ValoresFaturados, unknown>(
    [
      'valores_faturados',
      invoiceStatus,
      { arrGroups, arrHeadOffices, arrBrancches },
    ],
    () =>
      getValoresFaturados(invoiceStatus, {
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 10000, // 10 seconds
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useApolicesAVencer(
  page = 1,
  options: UseQueryOptions<Pagination<InsuranceList>, unknown> = {},
): UseQueryResult<Pagination<InsuranceList>> {
  const toast = useToast();
  const { groups, headOffices, branches } = useContext(FilterContext); // passar para os filhos
  const canSeeInsurances = useRoleCan({
    roles: ['administrador', 'operacional', 'suporte'],
  });

  const arrGroups = groups.map(e => e.value);
  const arrHeadOffices = headOffices.map(e => e.value);
  const arrBrancches = branches.map(e => e.value);
  return useQuery<Pagination<InsuranceList>, unknown>(
    ['apolices_a_vencer', page, { arrGroups, arrHeadOffices, arrBrancches }],
    () =>
      getApolicesAVencer(page, {
        groups: arrGroups,
        headoffices: arrHeadOffices,
        branches: arrBrancches,
      }),
    {
      staleTime: 10000, // 10 seconds
      enabled: canSeeInsurances,
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar dados',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}
